import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IssueCommentType, type IIssue } from '@d-orca/shared/trusstor';
import { CommentsList } from '../../IssuesPage/Dialog/CommentsList';
import { HubComposer } from '../../IssuesPage/Hub/HubComposer';
import { ImageLightbox } from '../../IssuesPage/Hub/ImageLightbox';
import styles from './IssueChat.module.css';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';

export interface IIssueChatProps {
  siteId: string;
  issue: IIssue;
}

/**
 * The issue comment thread + composer, reused verbatim from the Issues hub.
 * Comments live on `issue.comments`; the composer mutations patch the shared
 * `['monitor','issues',siteId]` cache, so new messages appear here optimistically
 * (the drawer's `issue` is re-derived from that same cache).
 */
export function IssueChat({ siteId, issue }: IIssueChatProps) {
  const { t } = useTranslation();
  const [lightboxAttachmentId, setLightboxAttachmentId] = useState<string | null>(null);

  // Image attachments in comment order, for lightbox navigation. Optimistic
  // image comments stash a `blob:` URL in content.value — pass it through so the
  // lightbox skips the remote fetch.
  const imageAttachments = useMemo(() => {
    return (issue.comments ?? [])
      .filter((c) => c.content?.type === IssueCommentType.IMAGE)
      .map((c) => ({
        attachmentId: c.content.value,
        localBlobUrl: c.content.value.startsWith('blob:') ? c.content.value : undefined,
      }));
  }, [issue.comments]);

  const lightboxIndex = lightboxAttachmentId
    ? imageAttachments.findIndex((a) => a.attachmentId === lightboxAttachmentId)
    : -1;

  return (
    <section className={styles.chat}>
      <h3 className={styles.title}>{t('activities.drawer.comments')}</h3>
      <ScrollArea className={styles.scroll}>
        <CommentsList
          comments={issue.comments ?? []}
          siteId={siteId}
          issueId={issue._id}
          onImageClick={setLightboxAttachmentId}
        />
      </ScrollArea>
      <HubComposer siteId={siteId} issueId={issue._id} />

      {lightboxIndex >= 0 && (
        <ImageLightbox
          siteId={siteId}
          issueId={issue._id}
          attachments={imageAttachments}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxAttachmentId(null)}
        />
      )}
    </section>
  );
}
